import { useState } from 'react';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';
import { SLA_DEFAULT_HOURS } from '../../../constants/raiseQueryForm';

/**
 * Get future ISO time string
 * @param {number} hoursFromNow - Hours from now
 * @returns {string} ISO time string
 */
const getFutureIsoTime = (hoursFromNow) =>
  new Date(Date.now() + hoursFromNow * 60 * 60 * 1000).toISOString();

/**
 * Hook to handle form submission for RaiseQueryForm
 * @param {Object} params - Hook parameters
 * @param {Object} params.formData - Form data object
 * @param {Array} params.attachments - Array of attachment files
 * @param {Function} params.validate - Validation function
 * @param {Function} params.setErrors - Setter for errors
 * @param {Function} params.resetForm - Function to reset form
 * @param {Function} params.onSuccess - Success callback
 * @returns {Object} Submit state and handler
 */
export const useRaiseQuerySubmit = ({
  formData,
  projectUsers = [],
  attachments,
  validate,
  setErrors,
  resetForm,
  onSuccess,
}) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const slaDueDate = getFutureIsoTime(SLA_DEFAULT_HOURS);

      // The selected assignee's own record from GetProjectAssignee (matched
      // by email, same key AssignToField uses) — carries PostName/PostShortName
      // straight from that API, so the query stores the exact post the
      // assignee was picked under for this project rather than a guess.
      const selectedAssignee = projectUsers.find(
        (u) => u.email === formData.assigneeId
      );

      // Transform field names to match backend expectations
      const backendPayload = {
        region: formData.region,
        projectName: formData.projectName,
        queryType: formData.queryType || 'GENERAL', // Default to GENERAL if not selected
        header: '', // Title field removed from UI, sending empty string to satisfy backend validation
        description: formData.queryDescription, // Backend expects 'description' not 'queryDescription'
        visualDescription: formData.visualDescription, // Visual description from Power BI
        assignee: formData.assigneeId, // Backend expects 'assignee' not 'assigneeId'
        priority: formData.priority || 'high', // Default to high if not selected
        slaDueDate,
        status: 'open',
        statusLabel: 'open',
        response: formData.statusResponse,
        postName: selectedAssignee?.PostName || '',
        postShortName: selectedAssignee?.PostShortName || '',
        // Raw AAI SSO token, same value sent to /auth/accessrights — lets
        // the backend fetch the raiser's reporting-post chain
        // (notifiedPostCodes) on their behalf.
        ssoToken: localStorage.getItem('ssoToken') || '',
      };

      let payload = backendPayload;
      let config = {};

      if (attachments.length > 0) {
        const multipartData = new FormData();
        Object.entries(backendPayload).forEach(([key, value]) => {
          multipartData.append(key, value ?? '');
        });
        attachments.forEach((file) => {
          multipartData.append('attachments', file);
        });
        payload = multipartData;
        config = { headers: { 'Content-Type': 'multipart/form-data' } };
      }

      const response = await api.post('/queries', payload, config);
      const queryData = response.data?.data?.query;
      const assigneeName = queryData?.assignee?.name || 'the assignee';
      
      showSnackbar(
        `Query created successfully! Email notifications have been sent to ${assigneeName}, all members of your region, MP, and Chairman.`,
        'success',
        { duration: 6000 } // Show for 6 seconds since message is longer
      );
      resetForm();
      onSuccess?.();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to raise query';
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleSubmit,
  };
};
